import { Injectable } from '@angular/core';
import { Animation, AnimationController } from '@ionic/angular';

@Injectable({
  providedIn: 'root'
})
export class AnimationService {
  private readonly uiEaseOut = 'cubic-bezier(0.23, 1, 0.32, 1)';
  private readonly reducedPageDuration = 160;


  constructor(
    private animationController: AnimationController
  ) { }

  dissolveAnimation() {
    return (baseEl: HTMLElement, opts?: any) => {
      if (this.prefersReducedMotion()) {
        return this.reducedPageAnimation(opts.enteringEl, opts.leavingEl);
      }
      const enteringAnimation = this.animationController.create()
        .addElement(opts.enteringEl)
        .beforeClearStyles(['opacity'])
        .duration(1000)
        .easing('ease-out')
        .fromTo('opacity', '0', '1');

      const leavingAnimation = this.animationController.create()
        .addElement(opts.leavingEl)
        .beforeClearStyles(['opacity'])
        .duration(1000)
        .easing('ease-out')
        .fromTo('opacity', '1', '0');

      return this.completePageTransition(this.animationController.create()
        .addAnimation([enteringAnimation, leavingAnimation]), opts.leavingEl);
    };
  }

  opacityModalEnterAnimation = (baseEl: HTMLElement) => {
    const root = baseEl.shadowRoot;

    const backdropAnimation = this.animationController
      .create()
      .addElement(root!.querySelector('ion-backdrop')!)
      .fromTo('opacity', '0.01', 'var(--backdrop-opacity)');

    const wrapperAnimation = this.animationController
      .create()
      .addElement(root!.querySelector('.modal-wrapper')!)
      .keyframes([
        { opacity: '0', transform: 'scale(1)' },
        { opacity: '1', transform: 'scale(1)' },
      ]);

    return this.animationController
      .create()
      .addElement(baseEl)
      .duration(200)
      .addAnimation([backdropAnimation, wrapperAnimation]);
  };

  opacityModalLeaveAnimation = (baseEl: HTMLElement) => {
    return this.opacityModalEnterAnimation(baseEl).direction('reverse');
  };

  dissolveDialogAnimation(baseEl: HTMLElement, backdropOpacity: number) {
    const backdropAnimation = this.animationController.create()
      .addElement(baseEl.querySelector('ion-backdrop')!)
      .fromTo('opacity', 0, backdropOpacity);

    const wrapperAnimation = this.animationController.create()
      .addElement(baseEl.querySelector('.alert-wrapper')!)
      .fromTo('opacity', 0, 1);

    return this.animationController.create()
      .addElement(baseEl)
      .duration(300)
      .easing('ease-out')
      .addAnimation([backdropAnimation, wrapperAnimation]);
  }

  dissolveDialogReversedAnimation(baseEl: HTMLElement, backdropOpacity: number) {
    const backdropAnimation = this.animationController.create()
      .addElement(baseEl.querySelector('ion-backdrop')!)
      .fromTo('opacity', backdropOpacity, 0);

    const wrapperAnimation = this.animationController.create()
      .addElement(baseEl.querySelector('.alert-wrapper')!)
      .fromTo('opacity', 1, 0);

    return this.animationController.create()
      .addElement(baseEl)
      .duration(180)
      .easing(this.uiEaseOut)
      .addAnimation([backdropAnimation, wrapperAnimation]);
  }

  slideInRightAnimation() {
    return (baseEl: HTMLElement, opts?: any) => {
      if (this.prefersReducedMotion()) {
        return this.reducedPageAnimation(opts.enteringEl, opts.leavingEl);
      }
      const enteringAnimation = this.animationController.create()
        .addElement(opts.enteringEl)
        .beforeClearStyles(['opacity'])
        .beforeRemoveClass('ion-page-invisible')
        .duration(300)
        .easing('ease-out')
        .fromTo('transform', 'translateX(100%)', 'translateX(0)')
        .beforeStyles({ 'z-index': '101' });

      const leavingAnimation = this.animationController.create()
        .addElement(opts.leavingEl)
        .beforeClearStyles(['opacity'])
        .duration(300)
        .easing('ease-out')
        .fromTo('transform', 'translateX(0)', 'translateX(-100%)')
        .beforeStyles({ 'z-index': '100' });

      return this.completePageTransition(this.animationController.create()
        .addAnimation([enteringAnimation, leavingAnimation]), opts.leavingEl);
    };
  }

  slideInLeftAnimation() {
    return (baseEl: HTMLElement, opts?: any) => {
      if (this.prefersReducedMotion()) {
        return this.reducedPageAnimation(opts.enteringEl, opts.leavingEl);
      }
      const enteringAnimation = this.animationController.create()
        .addElement(opts.enteringEl)
        .beforeClearStyles(['opacity'])
        .beforeRemoveClass('ion-page-invisible')
        .duration(300)
        .easing('ease-out')
        .fromTo('transform', 'translateX(-100%)', 'translateX(0)')
        .beforeStyles({ 'z-index': '100' });

      const leavingAnimation = this.animationController.create()
        .addElement(opts.leavingEl)
        .beforeClearStyles(['opacity'])
        .duration(300)
        .easing('ease-out')
        .fromTo('transform', 'translateX(0)', 'translateX(100%)')
        .beforeStyles({ 'z-index': '101' });

      return this.completePageTransition(this.animationController.create()
        .addAnimation([enteringAnimation, leavingAnimation]), opts.leavingEl);
    };
  }

  instantAnimation() {
    return (baseEl: HTMLElement, opts?: any) => {
      const enteringAnimation = this.animationController.create()
        .addElement(opts.enteringEl)
        .beforeClearStyles(['opacity'])
        .duration(0)
        .fromTo('opacity', '1', '1');

      const leavingAnimation = this.animationController.create()
        .addElement(opts.leavingEl)
        .beforeClearStyles(['opacity'])
        .duration(0)
        .fromTo('opacity', '0', '0');

      return this.completePageTransition(this.animationController.create()
        .addAnimation([enteringAnimation, leavingAnimation]), opts.leavingEl);
    };
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  private reducedPageAnimation(enteringEl: HTMLElement, leavingEl: HTMLElement): Animation {
    const entering = this.animationController.create()
      .addElement(enteringEl)
      .beforeClearStyles(['opacity', 'transform'])
      .beforeRemoveClass('ion-page-invisible')
      .duration(this.reducedPageDuration)
      .easing(this.uiEaseOut)
      .fromTo('opacity', '0', '1');
    const leaving = this.animationController.create()
      .addElement(leavingEl)
      .beforeClearStyles(['opacity', 'transform'])
      .duration(this.reducedPageDuration)
      .easing(this.uiEaseOut)
      .fromTo('opacity', '1', '0');
    return this.completePageTransition(this.animationController.create()
      .addAnimation([entering, leaving]), leavingEl);
  }

  private completePageTransition(animation: Animation, leavingEl: HTMLElement): Animation {
    // Ionic Angular hides cached pages on the next frame. Hide the outgoing page
    // before the animation is destroyed, so its restored transform cannot flash.
    // A reversed/cancelled transition must keep the current page visible.
    return animation.onFinish(currentStep => {
      if (currentStep === 1 && leavingEl) {
        leavingEl.classList.add('ion-page-hidden');
        leavingEl.setAttribute('aria-hidden', 'true');
      }
    });
  }

  progressBarAnimation(baseElement: HTMLElement, segmentElements: HTMLElement[], widthValues: string[]) {
    const segmentAnimations: any[] = [];

    segmentElements.forEach((element, index) => {
      if (element) {
        const segmentAnimation = this.animationController.create()
          .addElement(element)
          .duration(1000)
          .easing('ease-in-out')
          .delay(800)
          .fromTo('width', '0%', widthValues[index] || '0%');

        segmentAnimations.push(segmentAnimation);
      }
    });

    return this.animationController.create()
      .addElement(baseElement)
      .addAnimation(segmentAnimations);
  }
}
